import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useState, useMemo, useCallback } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Loader2,
  AlertCircle,
  Lock,
  RefreshCw,
  Clock,
  CheckCircle,
  ClipboardList,
  AlertTriangle,
  Receipt,
} from "lucide-react";
import { useRolePermissions, Permission } from "@/hooks/use-role-permissions";
import { useOrganization } from "@/hooks/use-organization";
import { usePendingTimeEntries } from "@/features/time-entry/org/hooks/usePendingTimeEntries";
import { useApprovedTimeEntries } from "@/features/time-entry/org/hooks/useApprovedTimeEntries";
import { useAwaitingSubmissionShifts } from "@/features/time-entry/org/hooks/useAwaitingSubmissionShifts";
import { useTimeEntryIssues } from "@/features/time-entry/org/hooks/useTimeEntryIssues";
import { useTimeEntryActions } from "@/features/time-entry/org/hooks/useTimeEntryActions";
import { TimeEntryReviewTable } from "@/features/time-entry/org/components/TimeEntryReviewTable";
import { TimeEntryHistoryTable } from "@/features/time-entry/org/components/TimeEntryHistoryTable";
import { AwaitingSubmissionTable } from "@/features/time-entry/org/components/AwaitingSubmissionTable";
import { TimeEntryIssuesTable } from "@/features/time-entry/org/components/TimeEntryIssuesTable";
import { TimeEntryFilters } from "@/features/time-entry/org/components/TimeEntryFilters";
import { TimeEntryRejectDialog } from "@/features/time-entry/org/components/TimeEntryRejectDialog";
import { CombinedTabSection } from "@/features/time-entry/org/components/CombinedTabSection";
import { groupByProviderAndFacility } from "@/features/time-entry/org/utils/group-time-entries";
import { ExpenseReviewTable } from "@/features/expense/org/components/ExpenseReviewTable";
import { ExpenseHistoryTable } from "@/features/expense/org/components/ExpenseHistoryTable";
import { usePendingExpenses } from "@/features/expense/org/hooks/usePendingExpenses";
import { useApprovedExpenses } from "@/features/expense/org/hooks/useApprovedExpenses";
import { startOfWeek, subWeeks, format, getISOWeek, getYear } from "date-fns";
import { cn } from "@/lib/utils";
import { z } from "zod";

export const Route = createFileRoute(
  "/_org/org/bu/$organizationBusinessUnitId/jobs/timesheets/",
)({
  component: TimesheetsPage,
  validateSearch: z.object({
    tab: z
      .enum(["pending", "history", "awaiting"])
      .optional()
      .default("pending"),
  }),
});

type TabType = "pending" | "history" | "awaiting";

function TimesheetsPage() {
  const { user } = Route.useRouteContext();
  const { organizationBusinessUnitId } = Route.useParams();
  const { hasPermission } = useRolePermissions(user);
  const canView = hasPermission(Permission.ViewTimesheets);
  const canApprove = hasPermission(Permission.ApproveTimesheets);
  const canManageInvoices = hasPermission(Permission.ManageInvoices);
  const { selectedOrgBusinessUnit } = useOrganization();
  const search = Route.useSearch();
  const navigate = useNavigate();

  const activeTab: TabType = (search.tab as TabType) || "pending";

  const setActiveTab = useCallback(
    (tab: TabType) => {
      navigate({
        to: "/org/bu/$organizationBusinessUnitId/jobs/timesheets",
        params: { organizationBusinessUnitId },
        search: (prev) => ({ ...prev, tab }),
        replace: true,
      });
    },
    [navigate, organizationBusinessUnitId],
  );

  // Pending tab filters (shared by time entries and expenses)
  const [pendingWeekKey, setPendingWeekKey] = useState<string | undefined>(
    undefined,
  );
  const [pendingFacilityId, setPendingFacilityId] = useState<
    string | undefined
  >(undefined);

  // History tab filters (shared by approved time entries and expenses)
  const [historyWeekKey, setHistoryWeekKey] = useState<string | undefined>(
    undefined,
  );
  const [historyFacilityId, setHistoryFacilityId] = useState<
    string | undefined
  >(undefined);
  const [historyProviderSearch, setHistoryProviderSearch] = useState<
    string | undefined
  >(undefined);

  // Awaiting tab filters
  const [awaitingWeekKey, setAwaitingWeekKey] = useState<string | undefined>(
    undefined,
  );
  const [awaitingFacilityId, setAwaitingFacilityId] = useState<
    string | undefined
  >(undefined);
  const [awaitingProviderSearch, setAwaitingProviderSearch] = useState<
    string | undefined
  >(undefined);

  // Selection state (pending tab only)
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  // Reject dialog state
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [rejectingEntry, setRejectingEntry] = useState<{
    id: string;
    providerName: string;
    facilityName: string;
    date: string;
  } | null>(null);

  // Data fetching - pending
  const {
    entries: pendingEntries,
    isLoading: isPendingLoading,
    error: pendingError,
    refetch: refetchPending,
  } = usePendingTimeEntries(selectedOrgBusinessUnit?.id, {
    weekKey: pendingWeekKey,
    facilityId: pendingFacilityId,
  });

  // Data fetching - approved
  const {
    entries: approvedEntries,
    isLoading: isApprovedLoading,
    error: approvedError,
    refetch: refetchApproved,
  } = useApprovedTimeEntries(selectedOrgBusinessUnit?.id, {
    weekKey: historyWeekKey,
    facilityId: historyFacilityId,
    providerSearch: historyProviderSearch,
  });

  // Data fetching - awaiting submission
  const {
    shifts: awaitingShifts,
    isLoading: isAwaitingLoading,
    error: awaitingError,
    refetch: refetchAwaiting,
  } = useAwaitingSubmissionShifts(selectedOrgBusinessUnit?.id, {
    weekKey: awaitingWeekKey,
    facilityId: awaitingFacilityId,
    providerSearch: awaitingProviderSearch,
  });

  const {
    issues: timeEntryIssues,
    isLoading: isIssuesLoading,
    error: issuesError,
    refetch: refetchIssues,
  } = useTimeEntryIssues(selectedOrgBusinessUnit?.id, {
    weekKey: awaitingWeekKey,
    facilityId: awaitingFacilityId,
    providerSearch: awaitingProviderSearch,
  });

  // Data fetching - pending expenses (uses same filters as pending time entries)
  const {
    expenses: pendingExpenses,
    isLoading: isPendingExpensesLoading,
    error: pendingExpensesError,
    refetch: refetchPendingExpenses,
  } = usePendingExpenses({
    organizationBusinessUnitId: selectedOrgBusinessUnit?.id ?? "",
    weekKey: pendingWeekKey,
    facilityId: pendingFacilityId,
  });

  // Data fetching - approved expenses (uses same filters as approved time entries)
  const {
    expenses: approvedExpenses,
    isLoading: isApprovedExpensesLoading,
    error: approvedExpensesError,
    refetch: refetchApprovedExpenses,
  } = useApprovedExpenses({
    organizationBusinessUnitId: selectedOrgBusinessUnit?.id ?? "",
    weekKey: historyWeekKey,
    facilityId: historyFacilityId,
    providerSearch: historyProviderSearch,
  });

  // Actions
  const {
    approveTimeEntries,
    rejectTimeEntry,
    resolveProviderIssue,
    isApproving,
    isRejecting,
    resolvingShiftId,
  } = useTimeEntryActions(() => {
    refetchPending();
    refetchApproved();
    refetchAwaiting();
    refetchIssues();
    setSelectedIds(new Set());
    setRejectDialogOpen(false);
    setRejectingEntry(null);
  });

  const handleResolveProviderIssue = (shiftId: string) => {
    if (!selectedOrgBusinessUnit?.id) {
      return;
    }
    void resolveProviderIssue({
      organizationBusinessUnitId: selectedOrgBusinessUnit.id,
      shiftId,
    }).catch(() => undefined);
  };

  // Group entries by provider and facility
  const groupedPendingEntries = useMemo(() => {
    return groupByProviderAndFacility(pendingEntries);
  }, [pendingEntries]);

  const groupedApprovedEntries = useMemo(() => {
    return groupByProviderAndFacility(approvedEntries);
  }, [approvedEntries]);

  const groupedAwaitingShifts = useMemo(() => {
    return groupByProviderAndFacility(awaitingShifts);
  }, [awaitingShifts]);

  // Extract unique facilities for filter dropdowns (combined from time entries and expenses)
  const pendingFacilities = useMemo(() => {
    const facilityMap = new Map<string, string>();
    pendingEntries.forEach((entry) => {
      facilityMap.set(entry.facilityId, entry.facilityName);
    });
    pendingExpenses.forEach((expense) => {
      facilityMap.set(expense.facilityId, expense.facilityName);
    });
    return Array.from(facilityMap.entries())
      .map(([id, name]) => ({ id, name }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [pendingEntries, pendingExpenses]);

  const approvedFacilities = useMemo(() => {
    const facilityMap = new Map<string, string>();
    approvedEntries.forEach((entry) => {
      facilityMap.set(entry.facilityId, entry.facilityName);
    });
    approvedExpenses.forEach((expense) => {
      facilityMap.set(expense.facilityId, expense.facilityName);
    });
    return Array.from(facilityMap.entries())
      .map(([id, name]) => ({ id, name }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [approvedEntries, approvedExpenses]);

  const awaitingFacilities = useMemo(() => {
    const facilityMap = new Map<string, string>();
    awaitingShifts.forEach((shift) => {
      facilityMap.set(shift.facilityId, shift.facilityName);
    });
    timeEntryIssues.forEach((issue) => {
      facilityMap.set(issue.facilityId, issue.facilityName);
    });
    return Array.from(facilityMap.entries())
      .map(([id, name]) => ({ id, name }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [awaitingShifts, timeEntryIssues]);

  // Generate week options (current week + 11 past weeks for history/awaiting)
  const weeks = useMemo(() => {
    const result = [];
    const numWeeks =
      activeTab === "history" || activeTab === "awaiting" ? 12 : 8;
    for (let i = 0; i < numWeeks; i++) {
      const weekStart = startOfWeek(subWeeks(new Date(), i), {
        weekStartsOn: 1,
      });
      const year = getYear(weekStart);
      const weekNumber = getISOWeek(weekStart);
      const key = `${year}-W${String(weekNumber).padStart(2, "0")}`;
      const label = `Week of ${format(weekStart, "MMM d, yyyy")}`;
      result.push({ key, label });
    }
    return result;
  }, [activeTab]);

  const handleApproveSelected = async () => {
    if (selectedIds.size === 0 || !selectedOrgBusinessUnit?.id) {
      return;
    }
    await approveTimeEntries({
      organizationBusinessUnitId: selectedOrgBusinessUnit.id,
      timeEntryIds: Array.from(selectedIds),
    });
  };

  const handleApproveOne = async (entryId: string) => {
    if (!selectedOrgBusinessUnit?.id) {
      return;
    }
    await approveTimeEntries({
      organizationBusinessUnitId: selectedOrgBusinessUnit.id,
      timeEntryIds: [entryId],
    });
  };

  const handleReject = (
    entryId: string,
    providerName: string,
    facilityName: string,
    date: string,
  ) => {
    setRejectingEntry({ id: entryId, providerName, facilityName, date });
    setRejectDialogOpen(true);
  };

  const handleConfirmReject = async (note: string) => {
    if (!rejectingEntry || !selectedOrgBusinessUnit?.id) {
      return;
    }
    await rejectTimeEntry({
      organizationBusinessUnitId: selectedOrgBusinessUnit.id,
      timeEntryId: rejectingEntry.id,
      rejectionNote: note,
    });
  };

  const handleClearPendingFilters = () => {
    setPendingWeekKey(undefined);
    setPendingFacilityId(undefined);
  };

  const handleClearHistoryFilters = () => {
    setHistoryWeekKey(undefined);
    setHistoryFacilityId(undefined);
    setHistoryProviderSearch(undefined);
  };

  const handleClearAwaitingFilters = () => {
    setAwaitingWeekKey(undefined);
    setAwaitingFacilityId(undefined);
    setAwaitingProviderSearch(undefined);
  };

  const tabs = [
    {
      key: "pending" as const,
      label: "Pending",
      icon: <ClipboardList className="h-4 w-4" />,
      count: pendingEntries.length + pendingExpenses.length,
    },
    {
      key: "history" as const,
      label: "History",
      icon: <CheckCircle className="h-4 w-4" />,
    },
    {
      key: "awaiting" as const,
      label: "Needs attention",
      icon: <AlertTriangle className="h-4 w-4" />,
      count: awaitingShifts.length + timeEntryIssues.length,
      countColor: "amber",
    },
  ];

  if (!canView) {
    return (
      <div className="flex-1 bg-slate-50">
        <div className="max-w-7xl mx-auto px-3 sm:px-4 lg:px-8 py-4 sm:py-8">
          <Card className="border-dashed">
            <CardContent className="p-8 md:p-12 flex flex-col items-center justify-center text-center gap-4">
              <div className="bg-slate-100 p-3 rounded-full">
                <Lock className="h-8 w-8 text-slate-600" />
              </div>
              <div>
                <h3 className="text-xl font-semibold text-slate-900 mb-1">
                  Access Restricted
                </h3>
                <p className="text-gray-600 max-w-md">
                  You don't have permission to view timesheets and expenses.
                  Contact your administrator to request access.
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 bg-slate-50">
      <div className="max-w-7xl mx-auto px-3 sm:px-4 lg:px-8 py-4 sm:py-8 space-y-4 sm:space-y-6">
        {/* Header */}
        <div className="space-y-2">
          <h1 className="text-3xl font-bold text-slate-900">
            Timesheets & Expenses
          </h1>
          <p className="text-slate-600 text-sm sm:text-base">
            {activeTab === "pending"
              ? "Review and approve submitted time entries and expenses from providers."
              : activeTab === "history"
                ? "View approved time entries and expenses history."
                : "Review missing submissions, provider-reported schedule issues, and rejected timesheets."}
          </p>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 p-1 bg-slate-100 rounded-lg w-full sm:w-fit">
          {tabs.map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={cn(
                "flex-1 sm:flex-none flex items-center justify-center sm:justify-start gap-1 sm:gap-2 px-2 sm:px-4 py-2 rounded-md text-xs sm:text-sm font-medium transition-colors",
                activeTab === tab.key
                  ? "bg-white text-slate-900 shadow-sm"
                  : "text-slate-600 hover:text-slate-900 hover:bg-white/50",
              )}
            >
              {tab.icon}
              <span>{tab.label}</span>
              {tab.count !== undefined && tab.count > 0 && (
                <span
                  className={cn(
                    "px-1.5 py-0.5 text-xs font-semibold rounded-full",
                    tab.countColor === "amber"
                      ? "bg-amber-100 text-amber-700"
                      : "bg-blue-100 text-blue-700",
                  )}
                >
                  {tab.count}
                </span>
              )}
            </button>
          ))}
        </div>

        {/* Filters */}
        <Card className="border border-slate-200 bg-white">
          <CardContent className="pt-5">
            {activeTab === "pending" ? (
              <TimeEntryFilters
                weekKey={pendingWeekKey}
                facilityId={pendingFacilityId}
                onWeekChange={setPendingWeekKey}
                onFacilityChange={setPendingFacilityId}
                onClearFilters={handleClearPendingFilters}
                facilities={pendingFacilities}
                weeks={weeks}
              />
            ) : activeTab === "history" ? (
              <TimeEntryFilters
                weekKey={historyWeekKey}
                facilityId={historyFacilityId}
                providerSearch={historyProviderSearch}
                onWeekChange={setHistoryWeekKey}
                onFacilityChange={setHistoryFacilityId}
                onProviderSearchChange={setHistoryProviderSearch}
                onClearFilters={handleClearHistoryFilters}
                facilities={approvedFacilities}
                weeks={weeks}
                showProviderSearch
              />
            ) : (
              <TimeEntryFilters
                weekKey={awaitingWeekKey}
                facilityId={awaitingFacilityId}
                providerSearch={awaitingProviderSearch}
                onWeekChange={setAwaitingWeekKey}
                onFacilityChange={setAwaitingFacilityId}
                onProviderSearchChange={setAwaitingProviderSearch}
                onClearFilters={handleClearAwaitingFilters}
                facilities={awaitingFacilities}
                weeks={weeks}
                showProviderSearch
              />
            )}
          </CardContent>
        </Card>

        {/* Content */}
        {activeTab === "pending" ? (
          // Pending tab content - Time Entries and Expenses stacked
          <div className="space-y-8">
            {/* Time Entries Section */}
            <CombinedTabSection
              title="Time Entries"
              icon={<Clock className="h-5 w-5" />}
              count={pendingEntries.length}
              countColor="blue"
            >
              {isPendingLoading ? (
                <Card className="border-dashed">
                  <CardContent className="p-8 flex items-center justify-center">
                    <Loader2 className="h-6 w-6 text-blue-600 animate-spin mr-2" />
                    <span className="text-sm text-slate-600">
                      Loading time entries...
                    </span>
                  </CardContent>
                </Card>
              ) : pendingError ? (
                <Card className="border-red-200 bg-red-50">
                  <CardContent className="p-6">
                    <div className="flex items-center gap-2 text-red-700">
                      <AlertCircle className="h-5 w-5" />
                      <span className="font-medium">
                        Failed to load time entries
                      </span>
                    </div>
                    <p className="text-sm text-red-600 mt-1">
                      {pendingError instanceof Error
                        ? pendingError.message
                        : "An error occurred"}
                    </p>
                    <Button
                      onClick={() => refetchPending()}
                      size="sm"
                      className="mt-3"
                    >
                      <RefreshCw className="h-4 w-4 mr-2" />
                      Retry
                    </Button>
                  </CardContent>
                </Card>
              ) : groupedPendingEntries.length > 0 ? (
                <TimeEntryReviewTable
                  groupedEntries={groupedPendingEntries}
                  selectedIds={selectedIds}
                  onSelectionChange={setSelectedIds}
                  onApproveSelected={handleApproveSelected}
                  onApproveOne={handleApproveOne}
                  onReject={handleReject}
                  isApproving={isApproving}
                  canApprove={canApprove}
                  organizationBusinessUnitId={
                    selectedOrgBusinessUnit?.id ?? organizationBusinessUnitId
                  }
                  onOrgNoteSaved={() => void refetchPending()}
                />
              ) : (
                <Card className="border-dashed">
                  <CardContent className="p-6 text-center text-sm text-slate-600">
                    <Clock className="h-8 w-8 mx-auto mb-2 text-slate-400" />
                    No pending time entries
                    {(pendingWeekKey || pendingFacilityId) && (
                      <p className="text-xs mt-1">Try adjusting your filters</p>
                    )}
                  </CardContent>
                </Card>
              )}
            </CombinedTabSection>

            {/* Expenses Section */}
            <CombinedTabSection
              title="Expenses"
              icon={<Receipt className="h-5 w-5" />}
              count={pendingExpenses.length}
              countColor="blue"
            >
              {isPendingExpensesLoading ? (
                <Card className="border-dashed">
                  <CardContent className="p-8 flex items-center justify-center">
                    <Loader2 className="h-6 w-6 text-blue-600 animate-spin mr-2" />
                    <span className="text-sm text-slate-600">
                      Loading expenses...
                    </span>
                  </CardContent>
                </Card>
              ) : pendingExpensesError ? (
                <Card className="border-red-200 bg-red-50">
                  <CardContent className="p-6">
                    <div className="flex items-center gap-2 text-red-700">
                      <AlertCircle className="h-5 w-5" />
                      <span className="font-medium">
                        Failed to load expenses
                      </span>
                    </div>
                    <p className="text-sm text-red-600 mt-1">
                      {pendingExpensesError instanceof Error
                        ? pendingExpensesError.message
                        : "An error occurred"}
                    </p>
                    <Button
                      onClick={() => refetchPendingExpenses()}
                      size="sm"
                      className="mt-3"
                    >
                      <RefreshCw className="h-4 w-4 mr-2" />
                      Retry
                    </Button>
                  </CardContent>
                </Card>
              ) : pendingExpenses.length > 0 && selectedOrgBusinessUnit?.id ? (
                <ExpenseReviewTable
                  expenses={pendingExpenses}
                  organizationBusinessUnitId={selectedOrgBusinessUnit.id}
                  canManage={canManageInvoices}
                />
              ) : (
                <Card className="border-dashed">
                  <CardContent className="p-6 text-center text-sm text-slate-600">
                    <Receipt className="h-8 w-8 mx-auto mb-2 text-slate-400" />
                    No pending expenses
                    {(pendingWeekKey || pendingFacilityId) && (
                      <p className="text-xs mt-1">Try adjusting your filters</p>
                    )}
                  </CardContent>
                </Card>
              )}
            </CombinedTabSection>
          </div>
        ) : activeTab === "history" ? (
          // History tab content - Time Entries and Expenses stacked
          <div className="space-y-8">
            {/* Time Entries History Section */}
            <CombinedTabSection
              title="Time Entries"
              icon={<Clock className="h-5 w-5" />}
              count={approvedEntries.length}
              countColor="green"
            >
              {isApprovedLoading ? (
                <Card className="border-dashed">
                  <CardContent className="p-8 flex items-center justify-center">
                    <Loader2 className="h-6 w-6 text-blue-600 animate-spin mr-2" />
                    <span className="text-sm text-slate-600">
                      Loading approved time entries...
                    </span>
                  </CardContent>
                </Card>
              ) : approvedError ? (
                <Card className="border-red-200 bg-red-50">
                  <CardContent className="p-6">
                    <div className="flex items-center gap-2 text-red-700">
                      <AlertCircle className="h-5 w-5" />
                      <span className="font-medium">
                        Failed to load approved entries
                      </span>
                    </div>
                    <Button
                      onClick={() => refetchApproved()}
                      size="sm"
                      className="mt-3"
                    >
                      <RefreshCw className="h-4 w-4 mr-2" />
                      Retry
                    </Button>
                  </CardContent>
                </Card>
              ) : groupedApprovedEntries.length > 0 ? (
                <TimeEntryHistoryTable
                  groupedEntries={groupedApprovedEntries}
                  organizationBusinessUnitId={
                    selectedOrgBusinessUnit?.id ?? organizationBusinessUnitId
                  }
                  canEditOrgNotes={canApprove}
                  onOrgNoteSaved={() => void refetchApproved()}
                />
              ) : (
                <Card className="border-dashed">
                  <CardContent className="p-6 text-center text-sm text-slate-600">
                    <CheckCircle className="h-8 w-8 mx-auto mb-2 text-slate-400" />
                    No approved time entries
                    {(historyWeekKey ||
                      historyFacilityId ||
                      historyProviderSearch) && (
                      <p className="text-xs mt-1">Try adjusting your filters</p>
                    )}
                  </CardContent>
                </Card>
              )}
            </CombinedTabSection>

            {/* Expenses History Section */}
            <CombinedTabSection
              title="Expenses"
              icon={<Receipt className="h-5 w-5" />}
              count={approvedExpenses.length}
              countColor="green"
            >
              {isApprovedExpensesLoading ? (
                <Card className="border-dashed">
                  <CardContent className="p-8 flex items-center justify-center">
                    <Loader2 className="h-6 w-6 text-blue-600 animate-spin mr-2" />
                    <span className="text-sm text-slate-600">
                      Loading approved expenses...
                    </span>
                  </CardContent>
                </Card>
              ) : approvedExpensesError ? (
                <Card className="border-red-200 bg-red-50">
                  <CardContent className="p-6">
                    <div className="flex items-center gap-2 text-red-700">
                      <AlertCircle className="h-5 w-5" />
                      <span className="font-medium">
                        Failed to load approved expenses
                      </span>
                    </div>
                    <Button
                      onClick={() => refetchApprovedExpenses()}
                      size="sm"
                      className="mt-3"
                    >
                      <RefreshCw className="h-4 w-4 mr-2" />
                      Retry
                    </Button>
                  </CardContent>
                </Card>
              ) : approvedExpenses.length > 0 ? (
                <ExpenseHistoryTable
                  expenses={approvedExpenses}
                  organizationBusinessUnitId={
                    selectedOrgBusinessUnit?.id ?? organizationBusinessUnitId
                  }
                  canEditOrgNotes={canManageInvoices}
                />
              ) : (
                <Card className="border-dashed">
                  <CardContent className="p-6 text-center text-sm text-slate-600">
                    <Receipt className="h-8 w-8 mx-auto mb-2 text-slate-400" />
                    No approved expenses
                    {(historyWeekKey ||
                      historyFacilityId ||
                      historyProviderSearch) && (
                      <p className="text-xs mt-1">Try adjusting your filters</p>
                    )}
                  </CardContent>
                </Card>
              )}
            </CombinedTabSection>
          </div>
        ) : // Awaiting submission tab content
        isAwaitingLoading || isIssuesLoading ? (
          <Card className="border-dashed">
            <CardContent className="p-8 sm:p-12 flex flex-col items-center justify-center text-center">
              <div className="bg-amber-100 p-4 rounded-full mb-6">
                <Loader2 className="h-8 w-8 text-amber-600 animate-spin" />
              </div>
              <h3 className="text-lg sm:text-xl font-semibold mb-2">
                Loading shifts
              </h3>
              <p className="text-gray-600 max-w-md text-sm sm:text-base">
                Please wait while we fetch shifts awaiting submission.
              </p>
            </CardContent>
          </Card>
        ) : awaitingError || issuesError ? (
          <Card className="border-red-200 bg-red-50">
            <CardContent className="p-8 sm:p-12 flex flex-col items-center justify-center text-center">
              <div className="bg-red-100 p-4 rounded-full mb-6">
                <AlertCircle className="h-8 w-8 text-red-600" />
              </div>
              <h3 className="text-lg sm:text-xl font-semibold mb-2">
                Failed to load shifts
              </h3>
              <p className="text-gray-600 mb-6 max-w-md text-sm sm:text-base">
                {awaitingError instanceof Error
                  ? awaitingError.message
                  : issuesError instanceof Error
                    ? issuesError.message
                    : "An error occurred. Please try again."}
              </p>
              <Button
                className="bg-blue-600 hover:bg-blue-700"
                onClick={() => {
                  refetchAwaiting();
                  refetchIssues();
                }}
              >
                <RefreshCw className="mr-2 h-4 w-4" />
                Retry
              </Button>
            </CardContent>
          </Card>
        ) : groupedAwaitingShifts.length > 0 || timeEntryIssues.length > 0 ? (
          <div className="space-y-8">
            <CombinedTabSection
              title="Reported / Rejected"
              icon={<AlertCircle className="h-5 w-5" />}
              count={timeEntryIssues.length}
              countColor="amber"
            >
              {timeEntryIssues.length > 0 ? (
                <TimeEntryIssuesTable
                  issues={timeEntryIssues}
                  canResolve={canApprove}
                  resolvingShiftId={resolvingShiftId}
                  onResolve={handleResolveProviderIssue}
                />
              ) : (
                <Card className="border-dashed">
                  <CardContent className="p-6 text-center text-sm text-slate-600">
                    No reported or rejected timesheets
                  </CardContent>
                </Card>
              )}
            </CombinedTabSection>

            <CombinedTabSection
              title="Awaiting submission"
              icon={<Clock className="h-5 w-5" />}
              count={awaitingShifts.length}
              countColor="amber"
            >
              {groupedAwaitingShifts.length > 0 ? (
                <AwaitingSubmissionTable
                  groupedShifts={groupedAwaitingShifts}
                />
              ) : (
                <Card className="border-dashed">
                  <CardContent className="p-6 text-center text-sm text-slate-600">
                    No timesheets awaiting submission
                  </CardContent>
                </Card>
              )}
            </CombinedTabSection>
          </div>
        ) : (
          <Card className="border-dashed">
            <CardContent className="p-8 sm:p-12 flex flex-col items-center justify-center text-center">
              <div className="bg-emerald-100 p-4 rounded-full mb-6">
                <CheckCircle className="h-8 w-8 text-emerald-600" />
              </div>
              <h3 className="text-lg sm:text-xl font-semibold mb-2">
                All timesheets submitted
              </h3>
              <p className="text-gray-600 max-w-md text-sm sm:text-base">
                {awaitingWeekKey || awaitingFacilityId || awaitingProviderSearch
                  ? "No shifts match your current filters."
                  : "All past shifts have timesheets submitted."}
              </p>
              {(awaitingWeekKey ||
                awaitingFacilityId ||
                awaitingProviderSearch) && (
                <Button
                  variant="outline"
                  onClick={handleClearAwaitingFilters}
                  className="mt-4"
                >
                  Clear Filters
                </Button>
              )}
            </CardContent>
          </Card>
        )}

        {/* Reject Dialog */}
        <TimeEntryRejectDialog
          open={rejectDialogOpen}
          onOpenChange={setRejectDialogOpen}
          onConfirm={handleConfirmReject}
          entryInfo={rejectingEntry}
          isRejecting={isRejecting}
        />
      </div>
    </div>
  );
}
